import React from 'react';

interface KeyboardProps {
  onKeyPress: (key: string) => void;
  onEnter: () => void;
  onBackspace: () => void;
  feedback: { [key: string]: 'green' | 'yellow' | 'gray' };
  useUmlauts?: boolean;
}

const Keyboard: React.FC<KeyboardProps> = ({
  onKeyPress,
  onEnter,
  onBackspace,
  feedback,
  useUmlauts = true
}) => {
  const getKeyColor = (key: string) => {
    const fb = feedback[key.toUpperCase()];
    if (fb === 'green') return 'bg-green-500 text-white';
    if (fb === 'yellow') return 'bg-yellow-500 text-white';
    if (fb === 'gray') return 'bg-gray-400 text-white';
    return 'bg-gray-200 text-gray-800';
  };

  // German QWERTZ layout
  const row1 = ['Q', 'W', 'E', 'R', 'T', 'Z', 'U', 'I', 'O', 'P'];
  const row2 = ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'];
  const row3 = ['Y', 'X', 'C', 'V', 'B', 'N', 'M'];
  const umlauts = useUmlauts ? ['Ü', 'Ö', 'Ä', 'ß'] : [];

  return (
    <div className="w-full max-w-full overflow-hidden px-1 mt-4 sm:mt-8 space-y-1">
      {/* Row 1: Q W E R T Z U I O P - 10 keys */}
      <div className="flex justify-center gap-0.5 flex-nowrap min-w-0">
        {row1.map((key) => (
          <button
            key={key}
            onClick={() => onKeyPress(key)}
            className={`w-[calc((100%-4.5px)/10)] aspect-square rounded-lg font-semibold text-sm sm:text-base md:text-lg transition-colors ${getKeyColor(key)} active:scale-95 touch-manipulation flex items-center justify-center shrink-0 min-w-0`}
            style={{ minHeight: '35px', maxHeight: '50px' }}
          >
            {key}
          </button>
        ))}
      </div>
      
      {/* Row 2: A S D F G H J K L - 9 keys (slightly indented) */}
      <div className="flex justify-center gap-0.5 flex-nowrap min-w-0" style={{ paddingLeft: '3%' }}>
        {row2.map((key) => (
          <button
            key={key}
            onClick={() => onKeyPress(key)}
            className={`w-[calc((100%-4px)/9)] aspect-square rounded-lg font-semibold text-sm sm:text-base md:text-lg transition-colors ${getKeyColor(key)} active:scale-95 touch-manipulation flex items-center justify-center shrink-0 min-w-0`}
            style={{ minHeight: '35px', maxHeight: '50px' }}
          >
            {key}
          </button>
        ))}
      </div>
      
      {/* Row 3: Y X C V B N M - 7 keys (more indented) */}
      <div className="flex justify-center gap-0.5 flex-nowrap min-w-0" style={{ paddingLeft: '6%' }}>
        {row3.map((key) => (
          <button
            key={key}
            onClick={() => onKeyPress(key)}
            className={`w-[calc((100%-3px)/7)] aspect-square rounded-lg font-semibold text-sm sm:text-base md:text-lg transition-colors ${getKeyColor(key)} active:scale-95 touch-manipulation flex items-center justify-center shrink-0 min-w-0`}
            style={{ minHeight: '35px', maxHeight: '50px' }}
          >
            {key}
          </button>
        ))}
      </div>
      
      {/* Row 4: SEND + Ü Ö Ä ß + Backspace */}
      <div className="flex justify-center gap-0.5 flex-nowrap min-w-0">
        {/* SEND Button */}
        <button
          onClick={onEnter}
          className="w-[calc((100%-3px)/6)] aspect-square rounded-lg font-semibold bg-blue-500 text-white hover:bg-blue-600 active:scale-95 text-xs sm:text-sm md:text-base touch-manipulation flex items-center justify-center shrink-0 min-w-0"
          style={{ minHeight: '35px', maxHeight: '50px' }}
        >
          SEND
        </button>
        
        {/* Umlauts: Ü Ö Ä ß */}
        {umlauts.map((key) => (
          <button
            key={key}
            onClick={() => onKeyPress(key)}
            className={`w-[calc((100%-3px)/6)] aspect-square rounded-lg font-semibold text-sm sm:text-base md:text-lg transition-colors ${getKeyColor(key)} active:scale-95 touch-manipulation flex items-center justify-center shrink-0 min-w-0`}
            style={{ minHeight: '35px', maxHeight: '50px' }}
          >
            {key}
          </button>
        ))}
        
        {/* Backspace Button */}
        <button
          onClick={onBackspace}
          className="w-[calc((100%-3px)/6)] aspect-square rounded-lg font-semibold bg-red-500 text-white hover:bg-red-600 active:scale-95 text-lg sm:text-xl md:text-2xl touch-manipulation flex items-center justify-center shrink-0 min-w-0"
          style={{ minHeight: '35px', maxHeight: '50px' }}
        >
          ⌫
        </button>
      </div>
    </div>
  );
};

export default Keyboard;

